import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ProjectSettings, TtsVoice, Enrollment, InviteRole, ProjectVersion, Chapter } from '@simultrain/shared';
import { TTS_VOICES } from '@simultrain/shared';
import { api } from '../api/client.js';

export function ProjectSettingsPage() {
  const { t } = useTranslation();
  const { projectId } = useParams<{ projectId: string }>();
  const [settings, setSettings] = useState<ProjectSettings | null>(null);
  const [keyDraft, setKeyDraft] = useState('');
  const [voice, setVoice] = useState<TtsVoice>('alloy');
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [domainsDraft, setDomainsDraft] = useState('');

  const [enrollments, setEnrollments] = useState<Enrollment[] | null>(null);
  const [versions, setVersions] = useState<ProjectVersion[] | null>(null);
  const [publishedChapters, setPublishedChapters] = useState<Chapter[] | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<InviteRole>('learner');
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [inviteBusy, setInviteBusy] = useState(false);

  useEffect(() => {
    if (!projectId) return;
    void api.getProjectSettings(projectId).then((s) => {
      setSettings(s);
      setVoice(s.ttsVoice);
      setDomainsDraft(s.allowedEmailDomains.join(', '));
    });
    void api.listEnrollments(projectId).then(setEnrollments);
    void api.getProject(projectId).then(async (p) => {
      setVersions(p.versions);
      const published = p.versions.find((v) => v.status === 'published');
      setPublishedChapters(published ? await api.listChapters(published.id) : null);
    });
  }, [projectId]);

  async function saveKey(e: React.FormEvent) {
    e.preventDefault();
    if (!projectId || !keyDraft.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await api.patchProjectSettings(projectId, { openaiKey: keyDraft.trim() });
      setSettings(updated);
      setKeyDraft('');
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1500);
    } catch (err) {
      setError(String(err));
    } finally {
      setSaving(false);
    }
  }

  async function removeKey() {
    if (!projectId) return;
    if (!window.confirm(t('settings.removeKeyConfirm'))) return;
    try {
      const updated = await api.patchProjectSettings(projectId, { openaiKey: null });
      setSettings(updated);
    } catch (err) {
      setError(String(err));
    }
  }

  async function changeVoice(v: TtsVoice) {
    if (!projectId) return;
    setVoice(v);
    try {
      const updated = await api.patchProjectSettings(projectId, { ttsVoice: v });
      setSettings(updated);
    } catch (err) {
      setError(String(err));
    }
  }

  async function saveDomains(e: React.FormEvent) {
    e.preventDefault();
    if (!projectId) return;
    const allowedEmailDomains = domainsDraft
      .split(',')
      .map((d) => d.trim().toLowerCase())
      .filter(Boolean);
    try {
      const updated = await api.patchProjectSettings(projectId, { allowedEmailDomains });
      setSettings(updated);
      setDomainsDraft(updated.allowedEmailDomains.join(', '));
    } catch (err) {
      setError(String(err));
    }
  }

  async function sendInvite(e: React.FormEvent) {
    e.preventDefault();
    if (!projectId || !inviteEmail.trim()) return;
    setInviteBusy(true);
    setInviteUrl(null);
    try {
      const { inviteUrl: url } = await api.createInvite(projectId, { email: inviteEmail.trim(), role: inviteRole });
      setInviteUrl(url);
      setInviteEmail('');
    } catch (err) {
      setError(String(err));
    } finally {
      setInviteBusy(false);
    }
  }

  async function removeEnrollment(userId: string) {
    if (!projectId) return;
    if (!window.confirm(t('enrollments.removeConfirm'))) return;
    try {
      await api.deleteEnrollment(projectId, userId);
      setEnrollments((prev) => prev?.filter((e) => e.userId !== userId) ?? prev);
    } catch (err) {
      setError(String(err));
    }
  }

  if (!settings) return <div className="empty">…</div>;

  return (
    <div style={{ maxWidth: 560 }}>
      <Link to={`/projects/${projectId}`} className="muted" style={{ fontSize: 13 }}>
        ← {t('projects.title')}
      </Link>
      <h2>{t('settings.title')}</h2>

      {error && <div className="error-banner">{error}</div>}

      <div className="card">
        <h3 style={{ marginTop: 0 }}>{t('settings.ttsSection')}</h3>
        <p className="muted" style={{ fontSize: 13 }}>{t('settings.ttsHint')}</p>

        <div style={{ marginBottom: 12 }}>
          <label>{t('settings.openaiKeyStatus')}</label>
          <div className="row" style={{ marginBottom: 8 }}>
            <span className={settings.hasOpenaiKey ? 'muted' : 'muted'}>
              {settings.hasOpenaiKey ? `✓ ${t('settings.keySet')}` : `— ${t('settings.keyNotSet')}`}
            </span>
            {settings.hasOpenaiKey && (
              <button type="button" className="danger" style={{ fontSize: 12, padding: '4px 10px' }} onClick={removeKey}>
                {t('settings.removeKey')}
              </button>
            )}
          </div>
        </div>

        <form onSubmit={saveKey}>
          <label>{settings.hasOpenaiKey ? t('settings.replaceKey') : t('settings.openaiKey')}</label>
          <input
            type="password"
            value={keyDraft}
            onChange={(e) => setKeyDraft(e.target.value)}
            placeholder="sk-..."
            autoComplete="off"
          />
          <button type="submit" disabled={saving || !keyDraft.trim()} style={{ marginTop: 12 }}>
            {savedFlash ? t('steps.saved') : t('steps.save')}
          </button>
        </form>

        <div style={{ marginTop: 16 }}>
          <label>{t('settings.ttsVoice')}</label>
          <select value={voice} onChange={(e) => void changeVoice(e.target.value as TtsVoice)}>
            {TTS_VOICES.map((v) => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>{t('enrollments.domainsSection')}</h3>
        <p className="muted" style={{ fontSize: 13 }}>{t('enrollments.domainsHint')}</p>
        <form onSubmit={saveDomains}>
          <label>{t('enrollments.domainsLabel')}</label>
          <input
            type="text"
            value={domainsDraft}
            onChange={(e) => setDomainsDraft(e.target.value)}
            placeholder="waterschaplimburg.nl"
          />
          <button type="submit" style={{ marginTop: 12 }}>{t('steps.save')}</button>
        </form>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>{t('enrollments.title')}</h3>
        <p className="muted" style={{ fontSize: 13 }}>{t('enrollments.hint')}</p>
        {versions && !versions.some((v) => v.status === 'published') && (
          <div className="zoom-warning">{t('projects.noPublishedVersionWarning')}</div>
        )}
        {versions && versions.some((v) => v.status === 'published') &&
          publishedChapters && !publishedChapters.some((c) => c.status === 'published') && (
            <div className="zoom-warning">{t('projects.noPublishedChapterWarning')}</div>
          )}

        {enrollments && enrollments.length === 0 && <div className="empty">{t('enrollments.empty')}</div>}
        {enrollments?.map((e) => (
          <div key={e.id} className="row" style={{ justifyContent: 'space-between', marginBottom: 6 }}>
            <div>
              <strong>{e.displayName}</strong>{' '}
              <span className="muted" style={{ fontSize: 13 }}>
                {e.email} ·{' '}
                {e.roles
                  .map((r) => (r === 'learner' ? t('enrollments.roleLearner') : r === 'tester' ? t('enrollments.roleTester') : r))
                  .join(', ')}{' '}
                · {t(`enrollments.via.${e.enrolledVia}`)}
              </span>
            </div>
            <button
              type="button"
              className="danger"
              style={{ fontSize: 12, padding: '4px 10px' }}
              onClick={() => void removeEnrollment(e.userId)}
            >
              {t('enrollments.remove')}
            </button>
          </div>
        ))}

        <form onSubmit={sendInvite} style={{ marginTop: 16 }}>
          <label>{t('enrollments.inviteLabel')}</label>
          <div className="row">
            <input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="naam@voorbeeld.nl"
              style={{ flex: 1 }}
            />
            <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value as InviteRole)} style={{ width: 140 }}>
              <option value="learner">{t('enrollments.roleLearner')}</option>
              <option value="tester">{t('enrollments.roleTester')}</option>
            </select>
            <button type="submit" disabled={inviteBusy || !inviteEmail.trim()}>
              {t('enrollments.invite')}
            </button>
          </div>
        </form>

        {inviteUrl && (
          <div style={{ marginTop: 12 }}>
            <label>{t('enrollments.inviteUrlLabel')}</label>
            <div className="row">
              <input type="text" value={inviteUrl} readOnly style={{ flex: 1 }} />
              <button type="button" onClick={() => void navigator.clipboard.writeText(inviteUrl)}>
                {t('enrollments.copy')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
